import type { Theme } from '../themeTypes'
import { getToken } from '../tokens/getToken'
import { comparisonNamedColorRegistry, namedColorRegistry } from './registry'
import {
  legacyBadgeColorMigrationMap,
  type ComparisonBadgeColor,
  type LegacyBadgeColor,
  type NamedColor,
  type NamedColorBorderVariant,
  type NamedColorDefinition,
  type TokenBadgeColor
} from './types'

export interface NamedColorConfig {
  backgroundColor: string
  borderColor?: Record<NamedColorBorderVariant, string>
}

const isTokenBadgeColor = (color: string): color is TokenBadgeColor => color in namedColorRegistry

const isComparisonBadgeColor = (color: string): color is ComparisonBadgeColor => color in comparisonNamedColorRegistry

const resolveDefinition = (color: NamedColor): NamedColorDefinition | undefined => {
  if (isTokenBadgeColor(color)) {
    return namedColorRegistry[color]
  }

  if (isComparisonBadgeColor(color)) {
    return comparisonNamedColorRegistry[color]
  }

  return undefined
}

export const migrateNamedColor = (color: string): NamedColor => {
  let migratedColor = color

  const legacyMigratedColor = legacyBadgeColorMigrationMap.get(color as LegacyBadgeColor)
  if (legacyMigratedColor != null) {
    migratedColor = legacyMigratedColor
  }

  return migratedColor as NamedColor
}

export const resolveNamedColor = (name: NamedColor, theme: Theme): string => {
  const migratedName = migrateNamedColor(name)
  const definition = resolveDefinition(migratedName)

  if (definition == null) {
    throw new Error(`Unknown named color: ${name}`)
  }

  return getToken(theme, definition.fill)
}

export const getNamedColorConfig = (name: NamedColor, theme: Theme): NamedColorConfig => {
  const migratedName = migrateNamedColor(name)
  const definition = resolveDefinition(migratedName)

  if (definition == null) {
    throw new Error(`Unknown named color: ${name}`)
  }

  const backgroundColor = getToken(theme, definition.fill)

  if (definition.border == null) {
    return { backgroundColor }
  }

  return {
    backgroundColor,
    borderColor: {
      lighter: getToken(theme, definition.border.lighter),
      darker: getToken(theme, definition.border.darker)
    }
  }
}
